import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { fetchVisitorAnalytics, fetchVisitorActivity } from "../../services";
import { Loader } from "../../components/Loader";
import { DownloadToExcel, Pagination } from "../Common";

// ── Event type metadata ──
const eventTypeMeta = {
  buy_now: { icon: "🛒", label: "Buy Now", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  ask_price: { icon: "💰", label: "Ask Price", color: "bg-amber-100 text-amber-700 border-amber-300" },
  Make_payment: { icon: "💳", label: "Make Payment", color: "bg-blue-100 text-blue-700 border-blue-300" },
  popup_close: { icon: "❌", label: "Popup Close", color: "bg-gray-100 text-gray-600 border-gray-300" },
  add_to_cart: { icon: "🛍️", label: "Add to Cart", color: "bg-violet-100 text-violet-700 border-violet-300" },
  view_product: { icon: "👁️", label: "View Product", color: "bg-sky-100 text-sky-700 border-sky-300" },
  search: { icon: "🔍", label: "Search", color: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  checkout: { icon: "📋", label: "Checkout", color: "bg-rose-100 text-rose-700 border-rose-300" },
};

const getEventMeta = (type) =>
  eventTypeMeta[type] || {
    icon: "📌",
    label: type?.replace(/_/g, " ") || "Unknown",
    color: "bg-gray-100 text-gray-700 border-gray-300",
  };

// ── Date helpers ──
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return "";
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
};

const truncate = (str, n) =>
  str && str.length > n ? `${str.substring(0, n)}…` : str || "—";

// ══════════════════════════════════════════════════════════════════
//  ACTIVITY DETAIL MODAL
// ══════════════════════════════════════════════════════════════════
const ActivityDetailModal = ({ visitor, activityData, loading, error, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  if (!visitor) return null;

  const userTypeLabel = visitor.is_logged_in ? "Registered User" : "Guest Visitor";

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-6 sm:pt-10 pb-6 sm:pb-10 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div
        ref={modalRef}
        className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-200 w-full max-w-5xl mx-3 sm:mx-4 overflow-hidden"
      >
        {/* ── Header ── */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-teal-600 to-cyan-600 px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white text-base sm:text-lg font-bold shadow-lg bg-white/20 flex-shrink-0">
              {(visitor.visitor_name || "G")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2 truncate">
                <span className="text-white">{visitor.visitor_name || "Guest Visitor"}</span>
                <span
                  className={`text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${
                    visitor.is_logged_in
                      ? "bg-green-400/20 text-green-100 border-green-300/40"
                      : "bg-gray-400/20 text-gray-100 border-gray-300/40"
                  }`}
                >
                  {userTypeLabel}
                </span>
              </h2>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs sm:text-sm text-teal-100 mt-0.5">
                {visitor.email && <span className="truncate max-w-[200px]">{visitor.email}</span>}
                {visitor.mobile && <span>• {visitor.mobile}</span>}
                {visitor.ip_address && <span className="hidden sm:inline">• {visitor.ip_address}</span>}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="p-4 sm:p-6">
          {loading && (
            <div className="flex items-center justify-center py-16 sm:py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm text-gray-500">Loading activity data…</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
          )}

          {!loading && !error && activityData && (
            <div className="space-y-5 sm:space-y-6">
              {/* ── Summary Cards ── */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {[
                  {
                    label: "Total Events",
                    value: activityData.summary?.total_events || 0,
                    gradient: "from-teal-50 to-cyan-50",
                    border: "border-teal-100",
                    text: "text-teal-600",
                    text2: "text-teal-800",
                  },
                  {
                    label: "Products Viewed",
                    value: activityData.top_products?.length || 0,
                    gradient: "from-blue-50 to-indigo-50",
                    border: "border-blue-100",
                    text: "text-blue-600",
                    text2: "text-blue-800",
                  },
                  {
                    label: "Pages Visited",
                    value: activityData.top_pages?.length || 0,
                    gradient: "from-amber-50 to-orange-50",
                    border: "border-amber-100",
                    text: "text-amber-600",
                    text2: "text-amber-800",
                  },
                  {
                    label: "Last Activity",
                    value: formatTimeAgo(activityData.summary?.last_activity),
                    gradient: "from-purple-50 to-violet-50",
                    border: "border-purple-100",
                    text: "text-purple-600",
                    text2: "text-purple-800",
                    isDate: true,
                  },
                ].map((card, i) => (
                  <div
                    key={i}
                    className={`bg-gradient-to-br ${card.gradient} border ${card.border} rounded-xl p-3 sm:p-4`}
                  >
                    <p className={`text-[10px] sm:text-xs font-semibold ${card.text} uppercase tracking-wider`}>
                      {card.label}
                    </p>
                    <p className={`text-xl sm:text-3xl font-extrabold ${card.text2} mt-1 ${
                      card.isDate ? "text-sm sm:text-base leading-tight" : ""
                    }`}>
                      {card.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* ── Event Breakdown + Top Products ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
                {/* Event Counts */}
                <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-md bg-teal-100 flex items-center justify-center">
                      <svg className="w-3 h-3 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </span>
                    Event Breakdown
                  </h3>
                  <div className="space-y-3">
                    {activityData.summary?.event_counts &&
                      Object.entries(activityData.summary.event_counts)
                        .sort(([, a], [, b]) => b - a)
                        .map(([eventType, count]) => {
                          const meta = getEventMeta(eventType);
                          const total = activityData.summary.total_events || 1;
                          const pct = ((count / total) * 100).toFixed(0);
                          return (
                            <div key={eventType}>
                              <div className="flex items-center justify-between text-xs sm:text-sm mb-1">
                                <span className="flex items-center gap-1.5 sm:gap-2 text-gray-700 font-medium truncate">
                                  <span>{meta.icon}</span>
                                  <span className="truncate">{meta.label}</span>
                                </span>
                                <span className="text-gray-500 text-[11px] flex-shrink-0 ml-2">
                                  {count} ({pct}%)
                                </span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-1.5 sm:h-2">
                                <div
                                  className="bg-gradient-to-r from-teal-500 to-cyan-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
                                  style={{ width: `${pct}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                    {(!activityData.summary?.event_counts ||
                      Object.keys(activityData.summary.event_counts).length === 0) && (
                      <p className="text-sm text-gray-400 text-center py-4">No event data available</p>
                    )}
                  </div>
                </div>

                {/* Top Products */}
                <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-md bg-blue-100 flex items-center justify-center">
                      <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </span>
                    Top Products
                  </h3>
                  {activityData.top_products && activityData.top_products.length > 0 ? (
                    <div className="space-y-2 sm:space-y-3">
                      {activityData.top_products.map((product, idx) => (
                        <div
                          key={product.product__uid || idx}
                          className="flex items-center gap-2 sm:gap-3 bg-gray-50 rounded-lg sm:rounded-xl p-2.5 sm:p-3 hover:bg-gray-100 transition-colors"
                        >
                          <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-teal-500 text-white text-[10px] sm:text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-800 truncate">
                              {product.product__equip_name || "Unknown Product"}
                            </p>
                            <p className="text-[11px] sm:text-xs text-gray-500">
                              {product.total} interaction{product.total > 1 ? "s" : ""}
                            </p>
                          </div>
                          <span className="text-xs sm:text-sm font-bold text-teal-700 bg-teal-50 px-2 py-1 sm:px-2.5 sm:py-1 rounded-lg flex-shrink-0">
                            {product.total}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-8">No product interactions recorded</p>
                  )}
                </div>
              </div>

              {/* ── Top Pages ── */}
              <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-md bg-amber-100 flex items-center justify-center">
                    <svg className="w-3 h-3 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </span>
                  Top Pages Visited
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                  {activityData.top_pages && activityData.top_pages.length > 0 ? (
                    activityData.top_pages.map((page, idx) => {
                      const maxTotal = Math.max(...activityData.top_pages.map((p) => p.total));
                      const barWidth = ((page.total / maxTotal) * 100).toFixed(0);
                      return (
                        <div key={idx} className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4">
                          <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                            <span className="text-[10px] sm:text-xs font-mono text-gray-600 truncate max-w-[140px] sm:max-w-[180px]" title={page.page}>
                              {page.page}
                            </span>
                            <span className="text-xs sm:text-sm font-bold text-teal-700 flex-shrink-0 ml-2">{page.total}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-gradient-to-r from-teal-400 to-cyan-500 h-1.5 rounded-full transition-all duration-500"
                              style={{ width: `${barWidth}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-6 col-span-full">No page visit data</p>
                  )}
                </div>
              </div>

              {/* ── Recent Activity Timeline ── */}
              <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-md bg-purple-100 flex items-center justify-center">
                    <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  Recent Activity Timeline
                  <span className="text-xs font-normal text-gray-400 ml-1">
                    ({activityData.recent_activity?.length || 0} events)
                  </span>
                </h3>
                {activityData.recent_activity && activityData.recent_activity.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-[17px] sm:left-[18px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-teal-400 via-cyan-400 to-teal-200 rounded-full"></div>
                    <div className="space-y-0">
                      {activityData.recent_activity.map((event, idx) => {
                        const meta = getEventMeta(event.event_type);
                        return (
                          <div key={idx} className="relative flex gap-3 sm:gap-5 pb-4 sm:pb-5 last:pb-0">
                            <div className="relative z-10 flex-shrink-0 mt-0.5">
                              <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-white border-2 border-teal-200 flex items-center justify-center text-xs sm:text-sm shadow-sm">
                                {meta.icon}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0 bg-gray-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-gray-100 hover:border-teal-200 hover:shadow-sm transition-all">
                              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1 sm:mb-1.5">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium border ${meta.color}`}
                                >
                                  {meta.label}
                                </span>
                                {event.button_name && (
                                  <span className="text-[10px] sm:text-xs text-gray-500 italic truncate max-w-[160px]">
                                    via &ldquo;{event.button_name}&rdquo;
                                  </span>
                                )}
                                <span className="text-[10px] sm:text-xs text-gray-400 ml-auto flex-shrink-0">
                                  {formatTimeAgo(event.created_date)}
                                </span>
                              </div>
                              <p className="text-xs sm:text-sm text-gray-700">
                                <span className="font-medium">Page:</span>{" "}
                                <span className="font-mono text-gray-600 break-all">{event.page || "/"}</span>
                              </p>
                              {event.product && (
                                <p className="text-xs sm:text-sm text-gray-700 mt-0.5">
                                  <span className="font-medium">Product:</span>{" "}
                                  <span className="text-gray-600">{event.product}</span>
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-400">
                                {event.browser && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    {event.browser}
                                  </span>
                                )}
                                {event.device && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                    {event.device}
                                  </span>
                                )}
                                {event.city && (
                                  <span className="hidden sm:flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {event.city}
                                  </span>
                                )}
                                <span className="hidden sm:inline text-gray-300 ml-auto">
                                  {formatDate(event.created_date)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                    <svg className="w-10 h-10 sm:w-12 sm:h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm">No recent activity recorded</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const UserAnalyticsActivity = () => {
  // ── Data state ──
  const [visitors, setVisitors] = useState([]);
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState("");

  // ── Pagination state ──
  const [currPage, setCurrPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [prevPage, setPrevPage] = useState(1);
  const [paging, setPaging] = useState({ total: 0, total_pages: 1 });
  // ── Modal state ──
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");

  // ── Sort state ──
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  // ── Filter state ──
  const [searchName, setSearchName] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [searchIp, setSearchIp] = useState("");
  const [browserFilter, setBrowserFilter] = useState([]);
  const [osFilter, setOsFilter] = useState([]);
  const [deviceFilter, setDeviceFilter] = useState([]);
  const [loggedInFilter, setLoggedInFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [countryFilter, setCountryFilter] = useState([]);
  const [cityFilter, setCityFilter] = useState([]);
  const [minEvents, setMinEvents] = useState("");
  const [maxEvents, setMaxEvents] = useState("");
  const [refererFilter, setRefererFilter] = useState("");

  // ── Dropdown toggles ──
  const [showBrowserDropdown, setShowBrowserDropdown] = useState(false);
  const [showOsDropdown, setShowOsDropdown] = useState(false);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [showLoggedInDropdown, setShowLoggedInDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const browserRef = useRef(null);
  const osRef = useRef(null);
  const deviceRef = useRef(null);
  const loggedInRef = useRef(null);
  const countryRef = useRef(null);
  const cityRef = useRef(null);
  const sortDirRef = useRef(sortDir);

  // Keep sort direction ref in sync (used inside functional updater to avoid stale closure)
  useEffect(() => { sortDirRef.current = sortDir; }, [sortDir]);

  // ── Fetch visitors (with server-side pagination) ──
  const fetchVisitors = useCallback(async (page, pageSize) => {
    setLoader(true);
    setError("");
    try {
      const params = { page, per_page: pageSize };
      const data = await fetchVisitorAnalytics(params);
      if (data) {
        // Server returns paginated response with { results, total, total_pages, ... }
        setVisitors(Array.isArray(data.results) ? data.results : []);
        setPaging({
          total: data.total || 0,
          total_pages: data.total_pages || 1,
        });
      } else {
        setVisitors([]);
        setPaging({ total: 0, total_pages: 1 });
      }
    } catch (err) {
      setError("Failed to load visitor analytics data.");
      setVisitors([]);
      setPaging({ total: 0, total_pages: 1 });
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchVisitors(currPage, perPage);
  }, [currPage, perPage, fetchVisitors]);

  // ── Click-outside for dropdowns ──
  useEffect(() => {
    const handler = (e) => {
      if (browserRef.current && !browserRef.current.contains(e.target)) setShowBrowserDropdown(false);
      if (osRef.current && !osRef.current.contains(e.target)) setShowOsDropdown(false);
      if (deviceRef.current && !deviceRef.current.contains(e.target)) setShowDeviceDropdown(false);
      if (loggedInRef.current && !loggedInRef.current.contains(e.target)) setShowLoggedInDropdown(false);
      if (countryRef.current && !countryRef.current.contains(e.target)) setShowCountryDropdown(false);
      if (cityRef.current && !cityRef.current.contains(e.target)) setShowCityDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Multi-filter toggle ──
  const toggleMultiFilter = (arr, setter, value) => {
    setter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  // ── Pagination handler ──
  const updatePage = (e) => {
    const name = e.currentTarget.name;
    if (name === "prev") {
      setCurrPage((prev) => (prev === 1 ? 1 : prev - 1));
      setPrevPage(currPage);
    } else if (name === "next") {
      setCurrPage((prev) => (prev === paging.total_pages ? prev : prev + 1));
      setPrevPage(currPage);
    } else {
      setCurrPage(parseInt(e.currentTarget.innerHTML));
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Clear all filters ──
  const clearFilters = () => {
    setSearchName("");
    setSearchEmail("");
    setSearchPhone("");
    setSearchIp("");
    setBrowserFilter([]);
    setOsFilter([]);
    setDeviceFilter([]);
    setLoggedInFilter("all");
    setDateFrom("");
    setDateTo("");
    setCountryFilter([]);
    setCityFilter([]);
    setMinEvents("");
    setMaxEvents("");
    setRefererFilter("");
    setSortKey("");
    setSortDir("asc");
    setCurrPage(1);
    setPrevPage(1);
  };

  // ── Sort handler ──
  const handleSort = (column) => {
    setSortKey((prevKey) => {
      // Use a fresh read of sortDir via ref to avoid stale closure
      if (prevKey === column && sortDirRef.current === "asc") {
        setSortDir("desc");
        return column;
      }
      setSortDir("asc");
      return column;
    });
  };

  // ── Row click → open modal ──
  const handleVisitorClick = useCallback(async (visitor) => {
    setSelectedVisitor(visitor);
    setActivityData(null);
    setActivityError("");
    setActivityLoading(true);
    try {
      const params = visitor.is_logged_in
        ? { user: visitor.user_uid || visitor.session_id }
        : { session_id: visitor.session_id };
      const data = await fetchVisitorActivity(params);
      setActivityData(data);
    } catch (err) {
      setActivityError(err.response?.data?.message || "Failed to load activity details.");
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedVisitor(null);
    setActivityData(null);
    setActivityError("");
  }, []);

  // ── Derived dropdown options ──
  const browserOptions = useMemo(() => [...new Set(visitors.map((v) => v.browser).filter(Boolean))].sort(), [visitors]);
  const osOptions = useMemo(() => [...new Set(visitors.map((v) => v.os).filter(Boolean))].sort(), [visitors]);
  const deviceOptions = useMemo(() => [...new Set(visitors.map((v) => v.device).filter(Boolean))].sort(), [visitors]);
  const countryOptions = useMemo(() => [...new Set(visitors.map((v) => v.country).filter(Boolean))].sort(), [visitors]);
  const cityOptions = useMemo(() => [...new Set(visitors.map((v) => v.city).filter(Boolean))].sort(), [visitors]);

  // ── Filtered + sorted data ──
  const filteredData = useMemo(() => {
    let data = [...visitors];

    // Text searches
    const byName = searchName.toLowerCase();
    const byEmail = searchEmail.toLowerCase();
    const byPhone = searchPhone.toLowerCase();
    const byIp = searchIp.toLowerCase();
    const byRef = refererFilter.toLowerCase();

    data = data.filter((v) => {
      if (byName && !(v.visitor_name || "").toLowerCase().includes(byName)) return false;
      if (byEmail && !(v.email || "").toLowerCase().includes(byEmail)) return false;
      if (byPhone && !String(v.mobile || v.phone || "").toLowerCase().includes(byPhone)) return false;
      if (byIp && !(v.ip_address || "").toLowerCase().includes(byIp)) return false;
      return true;
    });

    // Multi-select filters
    if (browserFilter.length > 0) data = data.filter((v) => browserFilter.includes(v.browser));
    if (osFilter.length > 0) data = data.filter((v) => osFilter.includes(v.os));
    if (deviceFilter.length > 0) data = data.filter((v) => deviceFilter.includes(v.device));
    if (loggedInFilter !== "all") {
      const wantLoggedIn = loggedInFilter === "logged_in";
      data = data.filter((v) => v.is_logged_in === wantLoggedIn);
    }
    if (countryFilter.length > 0) data = data.filter((v) => countryFilter.includes(v.country));
    if (cityFilter.length > 0) data = data.filter((v) => cityFilter.includes(v.city));

    // Date range
    if (dateFrom) {
      const from = new Date(dateFrom).getTime();
      data = data.filter((v) => v.last_visit && new Date(v.last_visit).getTime() >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo).setHours(23, 59, 59, 999);
      data = data.filter((v) => v.last_visit && new Date(v.last_visit).getTime() <= to);
    }

    // Events range
    if (minEvents) {
      const min = parseInt(minEvents, 10);
      data = data.filter((v) => (v.total_events || 0) >= min);
    }
    if (maxEvents) {
      const max = parseInt(maxEvents, 10);
      data = data.filter((v) => (v.total_events || 0) <= max);
    }

    // Referer
    if (byRef) data = data.filter((v) => (v.referer || "").toLowerCase().includes(byRef));

    // Sorting
    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1;
      data.sort((a, b) => {
        let aVal, bVal;
        switch (sortKey) {
          case "visitor_name": aVal = (a.visitor_name || "").toLowerCase(); bVal = (b.visitor_name || "").toLowerCase(); break;
          case "email": aVal = (a.email || "").toLowerCase(); bVal = (b.email || "").toLowerCase(); break;
          case "mobile": aVal = (a.mobile || a.phone || "").toLowerCase(); bVal = (b.mobile || b.phone || "").toLowerCase(); break;
          case "ip_address": aVal = (a.ip_address || "").toLowerCase(); bVal = (b.ip_address || "").toLowerCase(); break;
          case "browser": aVal = (a.browser || "").toLowerCase(); bVal = (b.browser || "").toLowerCase(); break;
          case "os": aVal = (a.os || "").toLowerCase(); bVal = (b.os || "").toLowerCase(); break;
          case "device": aVal = (a.device || "").toLowerCase(); bVal = (b.device || "").toLowerCase(); break;
          case "country": aVal = (a.country || "").toLowerCase(); bVal = (b.country || "").toLowerCase(); break;
          case "city": aVal = (a.city || "").toLowerCase(); bVal = (b.city || "").toLowerCase(); break;
          case "total_events": aVal = a.total_events || 0; bVal = b.total_events || 0; break;
          case "first_visit": aVal = a.first_visit ? new Date(a.first_visit).getTime() : 0; bVal = b.first_visit ? new Date(b.first_visit).getTime() : 0; break;
          case "last_visit": aVal = a.last_visit ? new Date(a.last_visit).getTime() : 0; bVal = b.last_visit ? new Date(b.last_visit).getTime() : 0; break;
          case "last_visit_activity_name": aVal = (a.last_visit_activity_name || "").toLowerCase(); bVal = (b.last_visit_activity_name || "").toLowerCase(); break;
          case "last_visit_page": aVal = (a.last_visit_page || "").toLowerCase(); bVal = (b.last_visit_page || "").toLowerCase(); break;
          default: return 0;
        }
        if (typeof aVal === "number" && typeof bVal === "number") return (aVal - bVal) * dir;
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      });
    }

    return data;
  }, [
    visitors, searchName, searchEmail, searchPhone, searchIp,
    browserFilter, osFilter, deviceFilter, loggedInFilter,
    dateFrom, dateTo, countryFilter, cityFilter,
    minEvents, maxEvents, refererFilter,
    sortKey, sortDir,
  ]);

  const filtersActive =
    searchName || searchEmail || searchPhone || searchIp ||
    browserFilter.length > 0 || osFilter.length > 0 || deviceFilter.length > 0 ||
    loggedInFilter !== "all" || dateFrom || dateTo ||
    countryFilter.length > 0 || cityFilter.length > 0 ||
    minEvents || maxEvents || refererFilter;

  // ── Sort icon component ──
  const SortIcon = ({ column }) => {
    const isActive = sortKey === column;
    return (
      <span
        className="inline-flex flex-col ml-0.5 flex-shrink-0 opacity-30 group-hover:opacity-100 transition-opacity cursor-pointer"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleSort(column); }}
      >
        <svg className={`w-2.5 h-2.5 -mb-px ${isActive && sortDir === "asc" ? "text-teal-600 opacity-100" : "text-gray-400"}`} fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 4l4 6H4z" />
        </svg>
        <svg className={`w-2.5 h-2.5 ${isActive && sortDir === "desc" ? "text-teal-600 opacity-100" : "text-gray-400"}`} fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 12L4 6h8z" />
        </svg>
      </span>
    );
  };

  // ── Multi-select dropdown header ──
  const MultiSelectDropdown = ({ label, options, selected, onToggle, show, setShow, refProp }) => {
    const headerLabel = selected.length === 0
      ? label
      : selected.length === 1
        ? `${label}: ${selected[0]}`
        : `${label} (${selected.length})`;

    const count = selected.length;

    return (
      <th ref={refProp} className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 last:border-r-0 select-none relative align-top cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[100px] whitespace-nowrap">
        <div className="flex items-center gap-1" onClick={() => setShow(!show)}>
          <span className="truncate">{headerLabel}</span>
          <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
          {count > 0 && (
            <span className="ml-auto bg-teal-500 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0">{count}</span>
          )}
        </div>
        {show && (
          <div
            className="absolute left-0 top-full z-50 mt-1 w-56 rounded-xl border border-gray-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">Select {label}</div>
            <div className="max-h-48 overflow-y-auto">
              {options.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500">No options</div>
              ) : (
                options.map((opt) => {
                  const checked = selected.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => onToggle(opt)}
                      className={`flex items-center w-full px-4 py-2 text-xs text-left hover:bg-gray-50 transition-colors ${
                        checked ? "bg-teal-50 text-teal-700 font-medium" : "text-gray-700"
                      }`}
                    >
                      <input type="checkbox" checked={checked} onChange={() => {}} className="mr-2 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                      {opt}
                    </button>
                  );
                })
              )}
            </div>
            <div className="border-t border-gray-100 px-4 py-2 flex justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-3 py-1 text-xs bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors font-medium">Done</button>
            </div>
          </div>
        )}
      </th>
    );
  };

  // ── Status dropdown ──
  const StatusDropdown = ({ label, selected, onSelect, show, setShow, refProp, options }) => {
    const headerLabel = selected === "all" ? label : selected === "logged_in" ? "Logged In: Yes" : "Logged In: No";
    return (
      <th ref={refProp} className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 last:border-r-0 select-none relative align-top cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[100px] whitespace-nowrap">
        <div className="flex items-center gap-1" onClick={() => setShow(!show)}>
          <span className="truncate">{headerLabel}</span>
          <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        {show && (
          <div
            className="absolute left-0 top-full z-50 mt-1 w-40 rounded-xl border border-gray-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { onSelect(opt.value); setShow(false); }}
                className={`block w-full px-4 py-2 text-left text-xs transition-colors ${
                  selected === opt.value ? "bg-teal-50 text-teal-700 font-medium" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </th>
    );
  };

  // ══════════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════════
  if (loader) return <Loader />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-5 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-gray-800 tracking-tight">
              User Analytics Activity
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              Track and analyze visitor activity across the platform
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Export */}
            <div className="flex-shrink-0 w-10 h-10">
              {/* <DownloadToExcel
                data={filteredData.map((v) => ({
                  "Visitor Name": v.visitor_name || "Unknown",
                  "Mobile": v.mobile || v.phone || "",
                  "Email": v.email || "",
                  "IP Address": v.ip_address,
                  "Browser": v.browser,
                  "OS": v.os,
                  "Device": v.device,
                  "Logged In": v.is_logged_in ? "Yes" : "No",
                  "First Visit": v.first_visit ? new Date(v.first_visit).toLocaleString("en-IN") : "",
                  "Last Visit": v.last_visit ? new Date(v.last_visit).toLocaleString("en-IN") : "",
                  "Total Events": v.total_events,
                  "Last Page": v.last_visit_page || "",
                  "Last Visited Page / Purpose": `${v.last_visit_page || ""}${v.last_visit_others && typeof v.last_visit_others === "string" ? ` — ${v.last_visit_others}` : ""}`,
                  "Last Activity": v.last_visit_activity_name || "",
                  "Country": v.country || "",
                  "State": v.state || "",
                  "City": v.city || "",
                  "Referer": v.referer || "",
                }))}
                filename="user-analytics-activity"
              /> */}
            </div>
          </div>
        </div>

        {/* ── Filters Card ── */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-4 lg:p-5 mb-5 sm:mb-6">
          {/* Quick filter row */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Name */}
            <div className="relative flex-1 min-w-[120px] max-w-[180px]">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text" placeholder="Name…" value={searchName} onChange={(e) => setSearchName(e.target.value)}
                className="w-full h-8 sm:h-9 pl-8 pr-2.5 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all"
              />
            </div>
            {/* Email */}
            <input
              type="text" placeholder="Email…" value={searchEmail} onChange={(e) => setSearchEmail(e.target.value)}
              className="flex-1 min-w-[120px] max-w-[180px] h-8 sm:h-9 px-2.5 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all"
            />
            {/* Phone */}
            <input
              type="text" placeholder="Phone…" value={searchPhone} onChange={(e) => setSearchPhone(e.target.value)}
              className="flex-1 min-w-[100px] max-w-[140px] h-8 sm:h-9 px-2.5 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 font-mono focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all"
            />
            {/* IP */}
            <input
              type="text" placeholder="IP…" value={searchIp} onChange={(e) => setSearchIp(e.target.value)}
              className="flex-1 min-w-[100px] max-w-[140px] h-8 sm:h-9 px-2.5 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 font-mono focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all"
            />
            {/* Referer */}
            <input
              type="text" placeholder="Referer…" value={refererFilter} onChange={(e) => setRefererFilter(e.target.value)}
              className="hidden lg:block flex-1 min-w-[120px] max-w-[180px] h-8 sm:h-9 px-2.5 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all"
            />
            {/* Events range */}
            <div className="flex items-center gap-1">
              <label className="text-[10px] sm:text-xs text-gray-500 font-medium whitespace-nowrap">Events</label>
              <input
                type="number" placeholder="Min" value={minEvents} onChange={(e) => setMinEvents(e.target.value)}
                className="w-12 sm:w-14 h-8 sm:h-9 px-1.5 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all"
              />
              <span className="text-gray-300 text-xs">–</span>
              <input
                type="number" placeholder="Max" value={maxEvents} onChange={(e) => setMaxEvents(e.target.value)}
                className="w-12 sm:w-14 h-8 sm:h-9 px-1.5 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all"
              />
            </div>
            {/* Date range */}
            <div className="flex items-center gap-1">
              <input
                type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="h-8 sm:h-9 px-2 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all w-[120px] sm:w-[130px]"
              />
              <span className="text-gray-300 text-xs">–</span>
              <input
                type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="h-8 sm:h-9 px-2 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm text-gray-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all w-[120px] sm:w-[130px]"
              />
            </div>
            {/* Total count + Clear */}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2.5 sm:px-3 py-1 rounded-full whitespace-nowrap">
                <span className="hidden sm:inline">Total: </span>
                <span className="font-semibold text-gray-700">{filteredData.length}</span>
              </span>
              {filtersActive && (
                <button
                  onClick={clearFilters}
                  className="h-8 sm:h-9 px-3 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-all active:scale-95 shadow-sm"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 text-red-700 text-xs sm:text-sm mb-5 sm:mb-6">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* ── Empty ── */}
        {!error && filteredData.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 sm:py-20 text-gray-400">
            <svg className="w-14 h-14 sm:w-16 sm:h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-base sm:text-lg font-semibold text-gray-500 mb-1">No Visitor Data</h3>
            <p className="text-xs sm:text-sm text-gray-400 max-w-md text-center">
              No visitor analytics data available yet. Data will appear once users visit the platform.
            </p>
          </div>
        )}

       {console.log("pazination", paging)}
        {!error && filteredData.length > 0 && (
          <>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" style={{ minWidth: "1550px" }}>
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                    <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 sticky left-0 bg-gradient-to-r from-gray-50 to-gray-100 z-10 min-w-[44px]">#</th>

                    <th
                      onClick={() => handleSort("visitor_name")}
                      className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 select-none relative cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[150px] whitespace-nowrap sticky left-[44px] bg-gradient-to-r from-gray-50 to-gray-100 z-10"
                    >
                      <div className="flex items-center gap-1">
                        <span>Visitor Name</span>
                        <SortIcon column="visitor_name" />
                      </div>
                    </th>

                    <th
                      onClick={() => handleSort("email")}
                      className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 select-none relative cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[180px] whitespace-nowrap"
                    >
                      <div className="flex items-center gap-1">
                        <span>Email</span>
                        <SortIcon column="email" />
                      </div>
                    </th>

                    <th
                      onClick={() => handleSort("mobile")}
                      className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 select-none relative cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[130px] whitespace-nowrap"
                    >
                      <div className="flex items-center gap-1">
                        <span>Mobile</span>
                        <SortIcon column="mobile" />
                      </div>
                    </th>

                    <th
                      onClick={() => handleSort("ip_address")}
                      className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 select-none relative cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[120px] whitespace-nowrap"
                    >
                      <div className="flex items-center gap-1">
                        <span>IP</span>
                        <SortIcon column="ip_address" />
                      </div>
                    </th>

                    <MultiSelectDropdown label="Browser" options={browserOptions} selected={browserFilter}
                      onToggle={(v) => toggleMultiFilter(browserFilter, setBrowserFilter, v)}
                      show={showBrowserDropdown} setShow={setShowBrowserDropdown} refProp={browserRef} />

                    <MultiSelectDropdown label="OS" options={osOptions} selected={osFilter}
                      onToggle={(v) => toggleMultiFilter(osFilter, setOsFilter, v)}
                      show={showOsDropdown} setShow={setShowOsDropdown} refProp={osRef} />

                    <MultiSelectDropdown label="Device" options={deviceOptions} selected={deviceFilter}
                      onToggle={(v) => toggleMultiFilter(deviceFilter, setDeviceFilter, v)}
                      show={showDeviceDropdown} setShow={setShowDeviceDropdown} refProp={deviceRef} />

                    <StatusDropdown label="Status" selected={loggedInFilter} onSelect={setLoggedInFilter}
                      show={showLoggedInDropdown} setShow={setShowLoggedInDropdown} refProp={loggedInRef}
                      options={[{ value: "all", label: "All" }, { value: "logged_in", label: "Logged In" }, { value: "not_logged_in", label: "Guest" }]} />

                    <th
                      onClick={() => handleSort("total_events")}
                      className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 select-none relative cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[80px] whitespace-nowrap"
                    >
                      <div className="flex items-center gap-1">
                        <span>Events</span>
                        <SortIcon column="total_events" />
                      </div>
                    </th>

                    <th
                      onClick={() => handleSort("last_visit_page")}
                      className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 select-none relative cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[140px] whitespace-nowrap"
                    >
                      <div className="flex items-center gap-1">
                        <span>Last Page</span>
                        <SortIcon column="last_visit_page" />
                      </div>
                    </th>

                    <th
                      onClick={() => handleSort("last_visit_page")}
                      className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 select-none relative cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[200px] whitespace-nowrap"
                    >
                      <div className="flex items-center gap-1">
                        <span>Last Visited Page / Purpose</span>
                        <SortIcon column="last_visit_page" />
                      </div>
                    </th>

                    <th
                      onClick={() => handleSort("last_visit_activity_name")}
                      className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 select-none relative cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[130px] whitespace-nowrap"
                    >
                      <div className="flex items-center gap-1">
                        <span>Last Activity</span>
                        <SortIcon column="last_visit_activity_name" />
                      </div>
                    </th>

                    <th
                      onClick={() => handleSort("first_visit")}
                      className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 select-none relative cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[140px] whitespace-nowrap"
                    >
                      <div className="flex items-center gap-1">
                        <span>First Visit</span>
                        <SortIcon column="first_visit" />
                      </div>
                    </th>

                    <th
                      onClick={() => handleSort("last_visit")}
                      className="group px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 select-none relative cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors min-w-[140px] whitespace-nowrap"
                    >
                      <div className="flex items-center gap-1">
                        <span>Last Visit</span>
                        <SortIcon column="last_visit" />
                      </div>
                    </th>

                    <MultiSelectDropdown label="Country" options={countryOptions} selected={countryFilter}
                      onToggle={(v) => toggleMultiFilter(countryFilter, setCountryFilter, v)}
                      show={showCountryDropdown} setShow={setShowCountryDropdown} refProp={countryRef} />

                    <MultiSelectDropdown label="City" options={cityOptions} selected={cityFilter}
                      onToggle={(v) => toggleMultiFilter(cityFilter, setCityFilter, v)}
                      show={showCityDropdown} setShow={setShowCityDropdown} refProp={cityRef} />

                    <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100 min-w-[140px] whitespace-nowrap">
                      <span>Referer</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredData.map((v, index) => {
                    // Helper to safely render last_visit_others
                    const formatOthers = (val) => {
                      if (!val || val === "{}" || val === "") return null;
                      if (typeof val === "string") return val;
                      try {
                        const parsed = JSON.stringify(val);
                        return parsed !== "{}" ? parsed : null;
                      } catch { return null; }
                    };

                    const othersText = formatOthers(v.last_visit_others);

                    // Build a unique key: session_id + user_uid (if exists)
                    const rowKey = v.session_id
                      ? `${v.session_id}-${v.user_uid || 'guest'}`
                      : `row-${index}`;

                    return (
                      <tr
                        key={rowKey}
                        onClick={() => handleVisitorClick(v)}
                        className="hover:bg-teal-50/60 transition-colors duration-100 even:bg-gray-50/40 cursor-pointer group"
                      >
                        <td className={`sticky left-0 z-10 px-3 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-gray-400 border-r border-gray-50 text-center ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} group-hover:bg-teal-50/60`}>
                          {index + 1}
                        </td>

                        <td className={`sticky left-[44px] z-10 px-3 py-2.5 sm:py-3 border-r border-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} group-hover:bg-teal-50/60`}>
                          <div className="flex items-center gap-2 sm:gap-2.5">
                            <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-white text-[10px] sm:text-xs font-bold flex-shrink-0 ${
                              v.is_logged_in ? "bg-gradient-to-br from-teal-400 to-cyan-500" : "bg-gradient-to-br from-gray-400 to-gray-500"
                            }`}>
                              {(v.visitor_name || "U")[0].toUpperCase()}
                            </div>
                            <span className="text-xs sm:text-sm font-medium text-gray-800 truncate max-w-[100px] sm:max-w-[140px]" title={v.visitor_name}>
                              {v.visitor_name || <span className="text-gray-400 italic">Unknown</span>}
                            </span>
                          </div>
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-600 border-r border-gray-50 max-w-[180px]">
                          <span className="truncate block" title={v.email}>
                            {v.email || <span className="text-gray-400 italic">—</span>}
                          </span>
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-700 font-mono border-r border-gray-50">
                          {v.mobile || v.phone || <span className="text-gray-400">—</span>}
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-600 font-mono border-r border-gray-50">
                          <span title={v.ip_address} className="truncate block max-w-[110px]">{v.ip_address || "—"}</span>
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 border-r border-gray-50">
                          <span className="inline-block px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 truncate max-w-[80px] sm:max-w-[100px] block">
                            {v.browser || "—"}
                          </span>
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-600 border-r border-gray-50">
                          {v.os || <span className="text-gray-400">—</span>}
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-600 border-r border-gray-50">
                          {v.device || <span className="text-gray-400">—</span>}
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 border-r border-gray-50">
                          {v.is_logged_in ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                              <span className="hidden sm:inline">User</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                              <span className="hidden sm:inline">Guest</span>
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-700 font-semibold border-r border-gray-50 text-center">
                          {v.total_events || 0}
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 border-r border-gray-50 max-w-[140px]">
                          {v.last_visit_page ? (
                            <span className="text-[10px] sm:text-xs font-mono text-gray-600 truncate block" title={v.last_visit_page}>
                              {v.last_visit_page}
                            </span>
                          ) : (
                            <span className="text-[10px] sm:text-xs text-gray-400 italic">—</span>
                          )}
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 border-r border-gray-50 max-w-[200px]">
                          <div className="flex flex-col gap-0.5">
                            {v.last_visit_page ? (
                              <span className="text-[10px] sm:text-xs font-mono text-gray-600 truncate block" title={v.last_visit_page}>
                                {v.last_visit_page}
                              </span>
                            ) : (
                              <span className="text-[10px] sm:text-xs text-gray-400 italic">—</span>
                            )}
                            {othersText && (
                              <span className="text-[10px] sm:text-xs text-gray-500 italic truncate" title={othersText}>
                                {othersText}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 border-r border-gray-50">
                          {v.last_visit_activity_name ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 truncate max-w-[110px] sm:max-w-[130px] block">
                              {v.last_visit_activity_name}
                            </span>
                          ) : (
                            <span className="text-[10px] sm:text-xs text-gray-400 italic">—</span>
                          )}
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 text-[10px] sm:text-xs text-gray-500 border-r border-gray-50 whitespace-nowrap">
                          {v.first_visit ? formatDate(v.first_visit) : "—"}
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 text-[10px] font-bold sm:text-xs text-gray-500 border-r border-gray-50 whitespace-nowrap">
                          {v.last_visit ? formatDate(v.last_visit) : "—"}
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-600 border-r border-gray-50">
                          {v.country || <span className="text-gray-400">—</span>}
                          {v.state ? <span className="text-gray-400 text-[10px] sm:text-xs">, {v.state}</span> : ""}
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-600 border-r border-gray-50">
                          {v.city || <span className="text-gray-400">—</span>}
                        </td>

                        <td className="px-3 py-2.5 sm:py-3 text-[10px] sm:text-xs text-gray-500 max-w-[140px]">
                          <span className="truncate block" title={v.referer}>{truncate(v.referer, 22) || "—"}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            total={paging.total}
            perPage={perPage}
            setPerPage={setPerPage}
            currPage={currPage}
            setCurrPage={setCurrPage}
            prevPage={prevPage}
            tPage={paging.total_pages}
            updatePage={updatePage}
          />
          </>
        )}

        {/* ── Modal ── */}
        {selectedVisitor && (
          <ActivityDetailModal
            visitor={selectedVisitor}
            activityData={activityData}
            loading={activityLoading}
            error={activityError}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </div>
  );
};

export default UserAnalyticsActivity;